<template>
  <div>
    <Head :title="displayName" />
    <h1 class="mb-8 text-3xl font-bold">
      <Link
        class="text-indigo-500 hover:text-indigo-800"
        :href="languagesRoute"
      >
        Języki
      </Link>
      <span class="font-medium text-indigo-400">/</span>
      {{ displayName }}
    </h1>
    <div class="w-full">
      <language-form
        v-model="form"
        v-model:editing-locale="editingLocale"
        :image-url="language.image_url"
        :show-active-toggle="false"
        @submit="submit"
      >
        <footer
          class="fixed bottom-0 left-0 right-0 z-30 border-t border-gray-100 bg-white/85 shadow-[0_-8px_30px_rgba(0,0,0,0.04)] backdrop-blur-md lg:left-64"
        >
          <div class="flex flex-wrap items-center gap-x-5 gap-y-3 px-4 py-3 sm:px-6 lg:px-8">
            <locale-editor-switch v-model="editingLocale" />
            <div class="hidden h-8 w-px shrink-0 bg-gray-200 sm:block" />
            <div class="flex shrink-0 items-center gap-2.5">
              <span class="text-sm font-medium text-gray-700">Aktywny</span>
              <toggle-switch
                v-model="form.language.active"
                :errors="form.errors['language.active'] || []"
              />
            </div>
            <div class="ml-auto flex shrink-0 items-center gap-2">
              <button
                class="inline-flex h-9 min-w-24 items-center justify-center gap-1.5 rounded-lg bg-red-50 px-4 text-sm font-medium text-red-700 transition-colors hover:bg-red-100"
                type="button"
                title="Usuń"
                @click="destroy"
              >
                <icon name="trash" class="h-3.5 w-3.5 stroke-current" />
                Usuń
              </button>
              <loading-button
                :loading="form.processing"
                class="inline-flex h-9 min-w-24 items-center justify-center rounded-lg bg-indigo-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
                type="submit"
              >
                Zapisz zmiany
              </loading-button>
            </div>
          </div>
        </footer>
      </language-form>
    </div>
  </div>
</template>

<script>
import { createLayoutHelper } from '@/utils/layoutHelper';

export default {
  layout: createLayoutHelper,
};
</script>

<script setup>
import { computed, getCurrentInstance, ref } from 'vue';
import { Head, Link, router, useForm } from '@inertiajs/vue3';
import Icon from '@/Shared/Icon.vue';
import LoadingButton from '@/Shared/LoadingButton.vue';
import LocaleEditorSwitch from '@/Shared/LocaleEditorSwitch.vue';
import ToggleSwitch from '@/Shared/ToggleSwitch.vue';
import LanguageForm from './Form.vue';
import omit from 'lodash/omit';
import { createLanguageRouteHelper } from '@/utils/createLanguageRouteHelper';

const { proxy } = getCurrentInstance();
const routes = proxy.$routes;
const editingLocale = ref('pl');

const props = defineProps({
  language: {
    type: Object,
    required: true,
  },
});

const languageData = omit(props.language, 'id', 'image_url', 'image_filename', 'data_pl', 'data_en');

const emptyLocale = () => ({ name: '' });

const form = useForm({
  language: {
    ...languageData,
    image: null,
    data_pl: { ...emptyLocale(), ...(props.language.data_pl || {}) },
    data_en: { ...emptyLocale(), ...(props.language.data_en || {}) },
  },
});

const displayName = computed(
  () => form.language.data_pl.name || form.language.data_en.name || '',
);

const languageRoutes = computed(() => createLanguageRouteHelper(null, routes));

const languagesRoute = computed(() => languageRoutes.value.index());

const submit = () => {
  form.transform((data) => {
    const transformed = { ...data };

    if (transformed.language.image === null) {
      delete transformed.language.image;
    }

    return transformed;
  }).put(languageRoutes.value.update(props.language.id));
};

const destroy = () => {
  if (confirm('Czy na pewno chcesz usunąć ten język?')) {
    router.delete(languageRoutes.value.destroy(props.language.id));
  }
};
</script>
